// src/components/FontPreview.js
import React from "react";
import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const CodeBox = ({ text }) => {
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
  };

  return (
    <Box
      sx={{
        position: "relative",
        p: 1,
        fontFamily: "monospace",
        fontSize: "0.8rem",
        whiteSpace: "pre-wrap",
        overflowWrap: "break-word",
        wordBreak: "break-word",
        bgcolor: (theme) =>
          theme.palette.mode === "dark" ? "#1e1e1e" : "#f5f5f5",
        border: "1px solid",
        borderColor: (theme) =>
          theme.palette.mode === "dark"
            ? theme.palette.grey[700]
            : theme.palette.grey[300],
        borderRadius: 2,
        maxHeight: "3.75em", // ~2 lines
        overflow: "auto",
        scrollbarWidth: "none", // Firefox
        "&::-webkit-scrollbar": { display: "none" }, // Chrome/Safari
      }}
    >
      {/* Copy button stays fixed inside the box */}
      <Tooltip title="Copy to clipboard">
        <IconButton
          size="small"
          onClick={handleCopy}
          sx={{
            position: "sticky",
            top: 0,
            float: "right",
            zIndex: 2,
            color: (theme) =>
              theme.palette.mode === "dark"
                ? theme.palette.grey[400]
                : theme.palette.grey[600],
          }}
        >
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Typography
        variant="body2"
        component="pre"
        sx={{
          m: 0,
          pr: 5,
          fontFamily: "monospace",
          fontSize: "0.8rem",
          whiteSpace: "pre-wrap",
          overflowWrap: "break-word",
          wordBreak: "break-word",
        }}
      >
        {text}
      </Typography>
    </Box>
  );
};

const FontPreview = ({
  avatarURL,
  avatarLabel,
  imageSrc,
  label,
  requiresPen,
  neohtml,
  neosignature,
}) => {
  return (
    <Box
      display="flex"
      flexDirection={{ xs: "column", md: "row" }}
      gap={2}
      sx={{ alignItems: "center", justifyContent: "center" }}
      p={3}
    >
      {/* Icon cell */}
      <Box
        component="img"
        src={avatarURL}
        alt={avatarLabel}
        sx={{
          width: 55, // 50px + 5px padding
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      />

      {/* Image cell */}
      <Box
        component="img"
        src={imageSrc}
        alt={label}
        sx={{
          width: "100%",
          maxWidth: 400,
          height: "auto",
          borderRadius: 2,
          flexShrink: 0,
        }}
      />

      {/* Right side */}
      <Box flex={1} display="flex" flexDirection="column" gap={1.5}
        sx={{
          width: "100%",
          maxWidth: 400,
        }}>
        <Typography variant="subtitle1" fontWeight="bold">
          {label}
          {requiresPen && <span style={{ color: "red" }}> *</span>}
        </Typography>

        <CodeBox text={neohtml} />
        <CodeBox text={neosignature} />
      </Box>
    </Box>
  );
};

export default FontPreview;
